<template>
    <div v-if="isVisible" class="error-summary alert alert-danger alert-dismissible fade show">
        <strong v-if="label">{{label}}</strong>

        <oc-button-input class="close" @click="open = false" aria-label="Close">
            <span aria-hidden="true">&times;</span>
        </oc-button-input>

        <ul>
            <li
                v-for="(fieldErrors, fieldNameLabel) in errors"
                :key="fieldNameLabel"
                v-bind="errorAttrs"
            >
                <template v-if="firstErrorOnly">
                    <span v-for="(fieldError, key) in fieldErrors" :key="key">
                        <b>{{fieldNameLabel}}:</b> <span v-html="fieldError" />
                    </span>
                </template>
                <template v-else>
                    <b>{{fieldNameLabel}}:</b>
                    <span v-for="(fieldError, key) in fieldErrors" :key="key" v-html="'<br>' + fieldError" />
                </template>
            </li>
        </ul>
    </div>
</template>
<script>
import {capitalize} from '@utils'
import debounce from 'lodash/debounce'

export default {
    name: 'oc-form-error-summary',
    inject: [
        'form'
    ],
    props: {
        startOpened: {
            type: Boolean,
            default: true
        },
        /**
         * Fields that are forbidden to be displayed in error summary
         * String|Regex supported
         */
        forbiddenFields: {
            type: Array,
            default: () => []
        },
        /**
         * Only the specified fields will be visible in error summary
         * String|Regex supported
         */
        allowedFields: {
            type: Array,
            default: () => []
        },
        label: {
            type: [String, Boolean],
            default: 'Form errors:'
        },
        useModelLabels: {
            type: Boolean,
            default: true
        },
        fieldLabels: {
            type: Object,
            default: () => {
                return {}
            }
        },
        firstErrorOnly: {
            type: Boolean,
            default: false
        },
        errorAttrs: {
            type: Object,
            default: () => {
                return {}
            }
        },
        scrollOnError: {
            type: Boolean,
            default: true
        },
        scrollSpeed: {
            type: Number,
            default: 500
        },
        scrollDebounce: {
            type: Number,
            default: 500
        }
    },
    data() {
        return {
            open: this.startOpened
        }
    },
    created() {
        this.form.validator().setBeforeValidate( (validator, field) => {
            if(field === undefined) { // global before validate - on form submission
                this.reset()
            }
        }, 'oc-error-summary')

        this.debouncedScroll = debounce(function() {
            if( ! this.isVisible ) {
                this.isScrollActivated = false
            } else {
                if( this.scrollOnError && ! this.isScrollActivated ) {
                    let VueScrollTo = require('vue-scrollto')

                    VueScrollTo.scrollTo('.error-summary', this.scrollSpeed, {
                        offset: -60
                    })

                    this.isScrollActivated = true
                }
            }
        }, this.scrollDebounce)
    },
    methods: {
        reset() {
            this.open = true
            this.isScrollActivated = false

            this.debouncedScroll()
        },
        _checkFieldName(fieldName) {

            let match = (schemaKey) => {

                schemaKey = String(schemaKey)

                if( schemaKey === String(fieldName) ) {
                    return true
                }
                else if( schemaKey.indexOf('/') === 0 ) {
                    let lastSlash = schemaKey.lastIndexOf("/");
                    let regex = new RegExp(schemaKey.slice(1, lastSlash), schemaKey.slice(lastSlash + 1));

                    if( regex.test(String(fieldName)) ) {
                        return true
                    }
                }

                return false
            }

            let result = true

            if( this.allowedFields.length > 0 ) {
                result = false

                for( let i in this.allowedFields ) {
                    let schemaKey = this.allowedFields[i]

                    result = match(schemaKey)

                    if( result ) {
                        break
                    }
                }
            }

            if( this.forbiddenFields.length > 0 ) {
                for( let i in this.forbiddenFields ) {
                    let schemaKey = this.forbiddenFields[i]

                    if( match(schemaKey) ) {
                        result = false
                        break
                    }
                }
            }

            return result
        }
    },
    computed: {
        isVisible() {
            return this.hasErrors && ( this.startOpened || this.open )
        },
        errors() {
            let allErrors = this.form.getValidator().errors().all( this.firstErrorOnly )

            let errors = {}

            for( let fieldName in allErrors ) {

                if( ! this._checkFieldName(fieldName) ) {
                    continue
                }

                let schemaFieldName = this.form.getValidator()._getSchemaFieldName(fieldName)
                let asteriskPath = this.form.getValidator()._getAsteriskPath(schemaFieldName)

                let fieldNameLabel = this.fieldLabels[fieldName] || this.fieldLabels[schemaFieldName] || this.fieldLabels[asteriskPath] // use label object desc

                if( ! isEmpty(fieldNameLabel) ) {

                    let indexes = fieldName.split('.').filter(function(value) {
                        return value.match(/[0-9]/gi, '') !== null
                    })

                    // replace indexes
                    let regex = /\{[0-9]\}*/g;
                    let match
                    while (match = regex.exec(fieldNameLabel)) {
                        let index = indexes[ match[0].replace(/\{|\}/gi, '') ]
                        if( typeof index === 'undefined' ) {
                            index = ''
                        }

                        fieldNameLabel = fieldNameLabel.replace(match[0], index)
                    }
                    //

                } else {
                    if( this.useModelLabels && this.form.hasFieldLabel(fieldName) ) {
                        fieldNameLabel = this.form.getFieldLabel(fieldName)
                    } else {
                        fieldNameLabel = fieldName.split('.').map( val => capitalize(val)).join(' ') // use field name
                    }
                }

                if( typeof errors[fieldName] === 'undefined' ) {
                    errors[fieldNameLabel] = []
                }

                let fieldErrors = allErrors[fieldName]
                if( Array.isArray(fieldErrors) ) {
                    errors[fieldNameLabel] = fieldErrors
                } else {
                    errors[fieldNameLabel].push(fieldErrors)
                }
            }

            return errors
        },
        hasErrors() {
            return this.form.getValidator().errors().any() && Object.keys(this.errors).length > 0
        }
    }
}
</script>
